/**
 * Custom artwork source Card — Phase 60-01 (§10 A2).
 *
 * Dedicated home for the per-org Method-C config: the front/back Shopify
 * line-item custom-attribute KEYS that carry the full-res print artwork URL
 * (e.g. `_pw_artwork__front`). This is NOT a row on the Product data lookup
 * card: that card maps Shopify METAFIELDS and its key field rejects the leading
 * underscore these line-item attribute keys use.
 *
 * Self-contained: drives its own tRPC query + mutation; not wired to any parent
 * save form. Admin-gated (reads gated to org members; the Save button + mutation
 * to admins). Unset = no Method-C capture = today's behavior.
 *
 * Shared editor (BLU-1399, D4 / G6): the onboarding "How do your custom files
 * arrive?" step embeds this same card (`embedded`): no card header, no Artwork
 * sizing group, and its save omits `resizeMode` so the stored sizing is never
 * changed from the wizard. The strings the embedded card shows read from
 * `settings` (`artworkSource.*`, en + zh); the Settings-only header and sizing
 * group stay literal English (N15). A save patches the `getConfig` cache from
 * the mutation's return instead of refetching (N14).
 *
 * Preview mockups (BLU-1521, Settings only): the line attributes the org's
 * customiser writes its front/back MOCKUP images to (staff-only pictures,
 * never printed). Hidden in the embedded wizard, and its save omits both
 * keys there, so the wizard can never clear them.
 *
 * Fallback attributes (BLU-1529, Settings only): one more attribute name per
 * print-file and preview field, for a store with two customisers. A line
 * uses a fallback only when it does not carry the main attribute. Same rules
 * as the preview keys: hidden in the wizard, sent only when changed.
 *
 * Allowed file hosts (BLU-1512, Settings only): the org's own list of hosts
 * its artwork links may come from, on top of the standard ones. Its own Save
 * (`setArtworkHosts`), so saving hosts never touches the keys or sizing. The
 * host rule lives on the server; the card shows the server's reason for each
 * entry it refused (en + zh).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Plus, RefreshCw, Save, X } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useRole } from '@/hooks/use-role';
import { trpc, type RouterOutputs } from '@/lib/trpc';

type ArtworkSourceConfig = RouterOutputs['artworkSourceConfig']['setConfig'];

/** BLU-1529: the four fallback key fields, in display order. */
const FALLBACK_FIELDS = [
  { field: 'frontAttrKeyFallback', id: 'artwork-front-key-fallback', label: 'frontLabel' },
  { field: 'backAttrKeyFallback', id: 'artwork-back-key-fallback', label: 'backLabel' },
  { field: 'previewFrontAttrKeyFallback', id: 'artwork-preview-front-key-fallback', label: 'previewFrontLabel' },
  { field: 'previewBackAttrKeyFallback', id: 'artwork-preview-back-key-fallback', label: 'previewBackLabel' },
] as const;
type FallbackField = (typeof FALLBACK_FIELDS)[number]['field'];
type FallbackKeys = Record<FallbackField, string>;
const EMPTY_FALLBACK_KEYS: FallbackKeys = {
  frontAttrKeyFallback: '',
  backAttrKeyFallback: '',
  previewFrontAttrKeyFallback: '',
  previewBackAttrKeyFallback: '',
};
type ArtworkHostRefusal = Extract<
  RouterOutputs['artworkSourceConfig']['setArtworkHosts'],
  { ok: false }
>['invalid'][number]['reason'];

/** Mirrors the server's MAX_ORG_ARTWORK_HOSTS (the column CHECK says the same). */
const MAX_ARTWORK_HOSTS = 20;

interface CustomArtworkSourceCardProps {
  /** Render inside another surface (the onboarding step): no card frame or header, no Artwork sizing. */
  embedded?: boolean;
  /** Called once with the saved config after a successful save. */
  onSaved?: (saved: ArtworkSourceConfig) => void;
}

const TOAST_ID = {
  saveSuccess: 'artwork-source-save-success',
  saveError: 'artwork-source-save-error',
  hostsSaveSuccess: 'artwork-hosts-save-success',
  hostsSaveError: 'artwork-hosts-save-error',
} as const;

export function CustomArtworkSourceCard({ embedded = false, onSaved }: CustomArtworkSourceCardProps = {}) {
  const { can, isAdmin } = useRole();

  // Mirror ProductDataLookupCard: hide entirely when the user lacks
  // org-settings read (also covers the 3PL cross-org-viewing case).
  if (!can('org_settings.read')) {
    return null;
  }

  return <CustomArtworkSourceCardInner admin={isAdmin()} embedded={embedded} onSaved={onSaved} />;
}

function CustomArtworkSourceCardInner({
  admin,
  embedded,
  onSaved,
}: { admin: boolean } & Required<Pick<CustomArtworkSourceCardProps, 'embedded'>> & Pick<CustomArtworkSourceCardProps, 'onSaved'>) {
  const { t } = useTranslation('settings');
  const utils = trpc.useUtils();

  const configQuery = trpc.artworkSourceConfig.getConfig.useQuery(undefined, {
    staleTime: 60_000,
  });

  const [frontKey, setFrontKey] = useState('');
  const [backKey, setBackKey] = useState('');
  const [previewFrontKey, setPreviewFrontKey] = useState('');
  const [previewBackKey, setPreviewBackKey] = useState('');
  const [fallbackKeys, setFallbackKeys] = useState<FallbackKeys>(EMPTY_FALLBACK_KEYS);
  const [resizeMode, setResizeMode] = useState<'platen' | 'source'>('platen');
  const [hasChanges, setHasChanges] = useState(false);

  // Seed the inputs from the loaded config — but NOT while the operator has
  // in-progress edits. configQuery has staleTime 60s, so a window refocus /
  // remount / invalidation fires a background refetch whose new data-reference
  // identity would otherwise re-run this effect and clobber unsaved edits (and
  // silently reset the dirty flag).
  //
  // The dirty flag is read through a ref, NOT a dependency: depending on
  // hasChanges would re-run the effect the instant the save onSuccess flips it
  // back to false, re-seeding from the still-stale pre-save cache and flashing
  // the inputs to their old values before the refetch lands. Keyed on the data
  // identity alone, the effect fires exactly when fresh config arrives — and
  // since onSuccess sets hasChanges=false in the same update that `setData`s
  // the saved config into the cache (a new data identity), the effect re-runs
  // on the saved values and re-seeds cleanly.
  const hasChangesRef = useRef(hasChanges);
  hasChangesRef.current = hasChanges;
  useEffect(() => {
    if (!configQuery.data || hasChangesRef.current) return;
    setFrontKey(configQuery.data.frontAttrKey ?? '');
    setBackKey(configQuery.data.backAttrKey ?? '');
    setPreviewFrontKey(configQuery.data.previewFrontAttrKey ?? '');
    setPreviewBackKey(configQuery.data.previewBackAttrKey ?? '');
    const data = configQuery.data;
    setFallbackKeys({
      frontAttrKeyFallback: data.frontAttrKeyFallback ?? '',
      backAttrKeyFallback: data.backAttrKeyFallback ?? '',
      previewFrontAttrKeyFallback: data.previewFrontAttrKeyFallback ?? '',
      previewBackAttrKeyFallback: data.previewBackAttrKeyFallback ?? '',
    });
    setResizeMode(configQuery.data.resizeMode ?? 'platen');
  }, [configQuery.data]);

  const setConfig = trpc.artworkSourceConfig.setConfig.useMutation({
    onSuccess: (saved) => {
      setHasChanges(false);
      utils.artworkSourceConfig.getConfig.setData(undefined, saved);
      toast.success(t('artworkSource.saved'), { id: TOAST_ID.saveSuccess });
      onSaved?.(saved);
    },
    onError: (err) => {
      toast.error(err.message || t('artworkSource.saveError'), {
        id: TOAST_ID.saveError,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Preview keys travel only when they CHANGED, so a save that leaves them
    // alone never sends them (and cannot fail on a database without the
    // preview columns yet). The router keeps anything omitted.
    const previewChanges: { previewFrontAttrKey?: string | null; previewBackAttrKey?: string | null } = {};
    const nextFront = previewFrontKey.trim() || null;
    const nextBack = previewBackKey.trim() || null;
    if (nextFront !== (configQuery.data?.previewFrontAttrKey ?? null)) previewChanges.previewFrontAttrKey = nextFront;
    if (nextBack !== (configQuery.data?.previewBackAttrKey ?? null)) previewChanges.previewBackAttrKey = nextBack;
    // BLU-1529: the fallback keys, same rule (only when changed).
    const fallbackChanges: Partial<Record<FallbackField, string | null>> = {};
    for (const { field } of FALLBACK_FIELDS) {
      const next = fallbackKeys[field].trim() || null;
      if (next !== (configQuery.data?.[field] ?? null)) fallbackChanges[field] = next;
    }
    setConfig.mutate({
      frontAttrKey: frontKey.trim() || null,
      backAttrKey: backKey.trim() || null,
      // Embedded (the wizard) never sends sizing, the preview keys or the
      // fallback keys: the router leaves anything omitted untouched.
      ...(embedded
        ? {}
        : { resizeMode, ...previewChanges, ...fallbackChanges }),
    });
  };

  const handleChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setHasChanges(true);
  };

  const disabled = !admin || setConfig.isPending;

  const body = (
    <>
      {configQuery.isLoading ? (
        <ArtworkSourceSkeleton embedded={embedded} loadingLabel={t('artworkSource.loading')} />
      ) : configQuery.isError ? (
        <div className="space-y-3">
          <p className="text-sm text-destructive">{t('artworkSource.loadError')}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void configQuery.refetch()}
            disabled={configQuery.isFetching}
            className="gap-1.5"
          >
            <RefreshCw
              className={configQuery.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
              aria-hidden="true"
            />
            {t('artworkSource.retry')}
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="artwork-front-key">{t('artworkSource.frontLabel')}</Label>
            <p className="text-sm text-muted-foreground">
              {t('artworkSource.example')} <code className="text-xs">_pw_artwork__front</code>
            </p>
            <Input
              id="artwork-front-key"
              value={frontKey}
              onChange={handleChange(setFrontKey)}
              placeholder="_pw_artwork__front"
              disabled={disabled}
              spellCheck={false}
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="artwork-back-key">{t('artworkSource.backLabel')}</Label>
            <p className="text-sm text-muted-foreground">
              {t('artworkSource.example')} <code className="text-xs">_pw_artwork__back</code>
            </p>
            <Input
              id="artwork-back-key"
              value={backKey}
              onChange={handleChange(setBackKey)}
              placeholder="_pw_artwork__back"
              disabled={disabled}
              spellCheck={false}
              autoComplete="off"
            />
          </div>

          {!embedded && (
            <fieldset className="space-y-4" aria-describedby="artwork-preview-desc">
              <div className="space-y-1">
                <legend className="text-sm font-medium">{t('artworkSource.preview.title')}</legend>
                <p id="artwork-preview-desc" className="text-sm text-muted-foreground">
                  {t('artworkSource.preview.description')}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="artwork-preview-front-key">{t('artworkSource.preview.frontLabel')}</Label>
                <p id="artwork-preview-front-hint" className="text-sm text-muted-foreground">
                  {t('artworkSource.preview.frontHint')}
                </p>
                <Input
                  id="artwork-preview-front-key"
                  value={previewFrontKey}
                  onChange={handleChange(setPreviewFrontKey)}
                  placeholder="_pw_preview"
                  aria-describedby="artwork-preview-front-hint"
                  disabled={disabled}
                  spellCheck={false}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="artwork-preview-back-key">{t('artworkSource.preview.backLabel')}</Label>
                <p id="artwork-preview-back-hint" className="text-sm text-muted-foreground">
                  {t('artworkSource.preview.backHint')}
                </p>
                <Input
                  id="artwork-preview-back-key"
                  value={previewBackKey}
                  onChange={handleChange(setPreviewBackKey)}
                  aria-describedby="artwork-preview-back-hint"
                  disabled={disabled}
                  spellCheck={false}
                  autoComplete="off"
                />
              </div>
            </fieldset>
          )}

          {!embedded && (
            <fieldset className="space-y-4" aria-describedby="artwork-fallback-desc">
              <div className="space-y-1">
                <legend className="text-sm font-medium">{t('artworkSource.fallback.title')}</legend>
                <p id="artwork-fallback-desc" className="text-sm text-muted-foreground">
                  {t('artworkSource.fallback.description')}
                </p>
              </div>
              {FALLBACK_FIELDS.map(({ field, id, label }) => (
                <div key={field} className="space-y-2">
                  <Label htmlFor={id}>{t(`artworkSource.fallback.${label}`)}</Label>
                  <Input
                    id={id}
                    value={fallbackKeys[field]}
                    onChange={handleChange((v) => setFallbackKeys((prev) => ({ ...prev, [field]: v })))}
                    disabled={disabled}
                    spellCheck={false}
                    autoComplete="off"
                  />
                </div>
              ))}
            </fieldset>
          )}

          {!embedded && (
            <div className="space-y-2">
              <Label id="artwork-resize-mode-label">Artwork sizing</Label>
              <p className="text-sm text-muted-foreground">
                How healed custom artwork is sized before it prints.
              </p>
              <RadioGroup
                aria-labelledby="artwork-resize-mode-label"
                value={resizeMode}
                onValueChange={(v) => {
                  setResizeMode(v as 'platen' | 'source');
                  setHasChanges(true);
                }}
                disabled={disabled}
                className="gap-3 pt-1"
              >
                <div className="flex items-start gap-3">
                  <RadioGroupItem
                    value="platen"
                    id="resize-mode-platen"
                    aria-describedby="resize-mode-platen-desc"
                    className="mt-0.5"
                  />
                  <div className="space-y-0.5">
                    <Label htmlFor="resize-mode-platen" className="font-medium">
                      Fit to platen
                    </Label>
                    <p id="resize-mode-platen-desc" className="text-sm text-muted-foreground">
                      Resizes and upscales artwork to fill the platen. Use for normal DTG and
                      DTF prints.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <RadioGroupItem
                    value="source"
                    id="resize-mode-source"
                    aria-describedby="resize-mode-source-desc"
                    className="mt-0.5"
                  />
                  <div className="space-y-0.5">
                    <Label htmlFor="resize-mode-source" className="font-medium">
                      Use file as-is
                    </Label>
                    <p id="resize-mode-source-desc" className="text-sm text-muted-foreground">
                      Keeps the customizer output&apos;s exact pixel dimensions and stamps 300
                      DPI. Use when the print size is set in the customizer output, e.g. wide
                      panels. Author customizer outputs at 300 DPI.
                    </p>
                  </div>
                </div>
              </RadioGroup>
              {resizeMode === 'source' && (
                <p className="text-xs text-muted-foreground">
                  Switch this on before the first heal of a design. Files already written at
                  platen size are kept until purged.
                </p>
              )}
            </div>
          )}

          {admin ? (
            <div className="flex items-center justify-end">
              <Button type="submit" disabled={disabled || !hasChanges} className="gap-1.5">
                {setConfig.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('artworkSource.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {t('artworkSource.save')}
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('artworkSource.adminOnly')}</p>
            )}
          </form>
        )}
      {!embedded && configQuery.data && (
        <ArtworkHostsSection admin={admin} savedHosts={configQuery.data.artworkHosts ?? []} />
      )}
    </>
  );

  if (embedded) return <div className="space-y-6">{body}</div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom artwork source</CardTitle>
        <CardDescription>
          The Shopify line-item attribute keys your customizer writes the full-resolution
          print artwork URL to. Leave blank to keep the current behaviour (no capture).
        </CardDescription>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}

interface HostRow {
  id: number;
  value: string;
}

/**
 * The org's own allowed file hosts. Rows are local until "Save hosts"; blank
 * rows are dropped before the save. A refused save keeps the rows and marks
 * each refused one with the server's reason.
 */
function ArtworkHostsSection({ admin, savedHosts }: { admin: boolean; savedHosts: string[] }) {
  const { t } = useTranslation('settings');
  const utils = trpc.useUtils();
  const nextId = useRef(0);
  const toRows = (hosts: string[]): HostRow[] => hosts.map((value) => ({ id: nextId.current++, value }));

  const [rows, setRows] = useState<HostRow[]>(() => toRows(savedHosts));
  const [errors, setErrors] = useState<Map<number, ArtworkHostRefusal>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  // Same rule as the keys above: re-seed from fresh data, never over edits.
  const hasChangesRef = useRef(hasChanges);
  hasChangesRef.current = hasChanges;
  const savedKey = savedHosts.join('\n');
  useEffect(() => {
    if (hasChangesRef.current) return;
    setRows(toRows(savedKey === '' ? [] : savedKey.split('\n')));
    setErrors(new Map());
  }, [savedKey]);

  // The row each submitted entry came from, so a refusal lands on its row.
  const submittedRowIds = useRef<number[]>([]);
  const setHosts = trpc.artworkSourceConfig.setArtworkHosts.useMutation({
    onSuccess: (result) => {
      if (!result.ok) {
        setErrors(new Map(result.invalid.map(({ index, reason }) => [submittedRowIds.current[index]!, reason])));
        toast.error(t('artworkSource.hosts.fixErrors'), { id: TOAST_ID.hostsSaveError });
        return;
      }
      setHasChanges(false);
      setErrors(new Map());
      setRows(toRows(result.hosts));
      utils.artworkSourceConfig.getConfig.setData(undefined, (prev) =>
        prev ? { ...prev, artworkHosts: result.hosts } : prev,
      );
      toast.success(t('artworkSource.hosts.saved'), { id: TOAST_ID.hostsSaveSuccess });
    },
    onError: (err) => {
      toast.error(err.message || t('artworkSource.hosts.saveError'), { id: TOAST_ID.hostsSaveError });
    },
  });

  const disabled = !admin || setHosts.isPending;
  const atMax = rows.length >= MAX_ARTWORK_HOSTS;

  const edit = (next: HostRow[]) => {
    setRows(next);
    setHasChanges(true);
  };
  const clearError = (id: number) => {
    if (!errors.has(id)) return;
    const next = new Map(errors);
    next.delete(id);
    setErrors(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const filled = rows.filter((row) => row.value.trim() !== '');
    submittedRowIds.current = filled.map((row) => row.id);
    setHosts.mutate({ hosts: filled.map((row) => row.value.trim()) });
  };

  const reasonText = (row: HostRow, reason: ArtworkHostRefusal): string =>
    t(`artworkSource.hosts.reason.${reason}`, {
      host: row.value.trim().toLowerCase().split('/')[0],
      defaultValue: t('artworkSource.hosts.reason.unknown'),
    });

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-4 border-t pt-6" aria-labelledby="artwork-hosts-title">
      <div className="space-y-1.5">
        <h3 id="artwork-hosts-title" className="text-sm font-medium leading-none">
          {t('artworkSource.hosts.title')}
        </h3>
        <p className="text-sm text-muted-foreground">{t('artworkSource.hosts.description')}</p>
        <p className="text-sm text-muted-foreground">{t('artworkSource.hosts.redirectNote')}</p>
        <p className="text-xs text-muted-foreground">{t('artworkSource.hosts.floorNote')}</p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('artworkSource.hosts.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row, i) => {
            const reason = errors.get(row.id);
            const inputId = `artwork-host-${row.id}`;
            const errorId = `${inputId}-error`;
            return (
              <li key={row.id} className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label htmlFor={inputId} className="sr-only">
                    {t('artworkSource.hosts.entryLabel', { number: i + 1 })}
                  </Label>
                  <Input
                    id={inputId}
                    value={row.value}
                    onChange={(e) => {
                      clearError(row.id);
                      edit(rows.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)));
                    }}
                    placeholder="files.example.com"
                    disabled={disabled}
                    spellCheck={false}
                    autoComplete="off"
                    autoCapitalize="none"
                    inputMode="url"
                    aria-invalid={reason ? true : undefined}
                    aria-describedby={reason ? errorId : undefined}
                    className="font-mono text-sm"
                  />
                  {admin && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        clearError(row.id);
                        edit(rows.filter((r) => r.id !== row.id));
                      }}
                      disabled={disabled}
                      aria-label={t('artworkSource.hosts.remove', { number: i + 1 })}
                      className="h-10 w-10 shrink-0"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>
                {reason && (
                  <p id={errorId} className="text-sm text-destructive">
                    {reasonText(row, reason)}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {admin ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => edit([...rows, { id: nextId.current++, value: '' }])}
              disabled={disabled || atMax}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('artworkSource.hosts.add')}
            </Button>
            {atMax && (
              <span className="text-xs text-muted-foreground">
                {t('artworkSource.hosts.max', { max: MAX_ARTWORK_HOSTS })}
              </span>
            )}
          </div>
          <Button type="submit" disabled={disabled || !hasChanges} className="gap-1.5">
            {setHosts.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('artworkSource.saving')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {t('artworkSource.hosts.save')}
              </>
            )}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('artworkSource.hosts.adminOnly')}</p>
      )}
    </form>
  );
}

// Loading skeleton — mirrors the loaded form shape (two key fields + the
// artwork-sizing radio group, not in embedded mode) so the card doesn't reflow
// when the config lands. motion-safe pulse only.
function ArtworkSourceSkeleton({ embedded, loadingLabel }: { embedded: boolean; loadingLabel: ReactNode }) {
  return (
    <>
      {/* aria-hidden wraps ONLY the visual placeholder — keeping the sr-only
          announcement a sibling so it stays in the accessibility tree
          (aria-hidden hides every descendant). */}
      <div
        className="space-y-6 motion-safe:animate-pulse motion-reduce:animate-none"
        aria-hidden="true"
      >
        {[0, 1].map((i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 w-48 rounded bg-muted" />
            <div className="h-3 w-40 rounded bg-muted" />
            <div className="h-10 w-full rounded bg-muted" />
          </div>
        ))}
        {/* Artwork-sizing radio group placeholder: label + hint + two options */}
        {!embedded && (
          <div className="space-y-2">
            <div className="h-4 w-32 rounded bg-muted" />
            <div className="h-3 w-64 rounded bg-muted" />
            {[0, 1].map((i) => (
              <div key={i} className="flex items-start gap-3 pt-1">
                <div className="h-4 w-4 rounded-full bg-muted" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-4 w-28 rounded bg-muted" />
                  <div className="h-3 w-full rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <span className="sr-only">{loadingLabel}</span>
    </>
  );
}
